import { useState } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { format } from 'date-fns';
import { Trash2, Wallet } from 'lucide-react';
import {
    useExpensePayments,
    useCreateExpensePayment,
    useDeleteExpensePayment,
    BudgetExpense
} from '@/hooks/useBudget';

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
    }).format(amount);
};

export function ExpensePaymentsManager({ expense }: { expense: BudgetExpense }) {
    const [open, setOpen] = useState(false);
    const { data: payments = [] } = useExpensePayments(open ? expense.id : undefined);
    const createPayment = useCreateExpensePayment();
    const deletePayment = useDeleteExpensePayment();

    const [paymentForm, setPaymentForm] = useState({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        notes: '',
    });

    const totalPaid = payments.reduce((sum, p) => sum + Number(p.amount), 0);
    const totalDebt = Number(expense.amount) - totalPaid;

    const handleCreatePayment = () => {
        const amount = parseFloat(paymentForm.amount.replace(/,/g, ''));
        if (!isNaN(amount) && amount > 0) {
            createPayment.mutate({
                expense_id: expense.id,
                amount,
                payment_date: paymentForm.payment_date,
                notes: paymentForm.notes.trim() || undefined,
            });
            setPaymentForm({
                amount: '',
                payment_date: new Date().toISOString().split('T')[0],
                notes: '',
            });
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-primary">
                    <Wallet className="w-4 h-4" />
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle>Pagos Parciales: {expense.description}</DialogTitle>
                </DialogHeader>

                <div className="grid grid-cols-2 gap-4 my-2 p-3 bg-muted rounded-lg">
                    <div>
                        <span className="text-xs text-muted-foreground block">Costo Real</span>
                        <span className="font-semibold">{formatCurrency(Number(expense.amount))}</span>
                    </div>
                    <div>
                        <span className="text-xs text-muted-foreground block">Adeudo Pendiente</span>
                        <span className={`font-semibold ${totalDebt > 0 ? "text-destructive" : "text-green-600"}`}>
                            {formatCurrency(totalDebt)}
                        </span>
                    </div>
                </div>

                <div className="space-y-4 pt-4 border-t">
                    <h4 className="font-medium text-sm">Registrar Nuevo Pago</h4>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Monto</Label>
                            <Input
                                placeholder="ej: 5,000"
                                value={paymentForm.amount}
                                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Fecha</Label>
                            <Input
                                type="date"
                                value={paymentForm.payment_date}
                                onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Notas (opcional)</Label>
                        <Input
                            placeholder="ej: Transferencia Banco X"
                            value={paymentForm.notes}
                            onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                        />
                    </div>
                    <Button onClick={handleCreatePayment} disabled={!paymentForm.amount} className="w-full">
                        Registrar Pago
                    </Button>
                </div>

                {payments.length > 0 && (
                    <div className="mt-6">
                        <h4 className="font-medium text-sm mb-3">Historial de Pagos</h4>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Fecha</TableHead>
                                    <TableHead>Monto</TableHead>
                                    <TableHead>Notas</TableHead>
                                    <TableHead></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {payments.map((p) => (
                                    <TableRow key={p.id}>
                                        <TableCell>{format(new Date(p.payment_date), 'dd/MM/yyyy')}</TableCell>
                                        <TableCell className="font-medium">{formatCurrency(p.amount)}</TableCell>
                                        <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate">
                                            {p.notes || '-'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="icon" onClick={() => deletePayment.mutate(p.id)} className="text-destructive h-6 w-6">
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
